import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Bell, CheckCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import type { Notification } from "@/types";

export default function NotificationsPage() {
  const { api } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications", { params: { limit: 50 } });
      setNotifications(res.data.items);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id: string) => {
    try {
      await api.put(`/notifications/${id}/read`);
      fetchNotifications();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      toast.success("All marked as read");
      fetchNotifications();
    } catch {}
  };

  const generateNotifs = async () => {
    try {
      const res = await api.post("/notifications/generate");
      toast.success(res.data.message);
      fetchNotifications();
    } catch {
      toast.error("Failed");
    }
  };

  const typeColors: Record<string, string> = {
    overdue: "bg-red-100 text-red-800",
    due_soon: "bg-yellow-100 text-yellow-800",
    reservation_available: "bg-blue-100 text-blue-800",
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Notifications
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={generateNotifs} data-testid="generate-notifs-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Generate
          </Button>
          <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4 mr-1.5" /> Mark All Read
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : notifications.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No notifications</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => (
            <Card key={n.id} className={`border-0 shadow-sm transition-all ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
              <CardContent className="flex items-center justify-between py-4 px-5">
                <div className="flex items-center gap-3">
                  {!n.read && <div className="w-2 h-2 rounded-full bg-[var(--beat)] shrink-0" />}
                  <div>
                    <p className="text-sm">{n.message}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${typeColors[n.type] || "bg-gray-100 text-gray-800"} hover:${typeColors[n.type] || "bg-gray-100"} text-xs`}>
                    {n.type?.replace("_", " ")}
                  </Badge>
                  {!n.read && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => markRead(n.id)}>
                      Mark read
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
